import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import { makeGetCurrentUser } from '../selectors/users';
import { makeGetSortedActiveProjectTaskGroups } from '../selectors/task-groups';

import { createAlert } from '../services/alert-service';
import { encodeCronString, decodeCronString } from '../utils/recurring';

import Popout from './popout';
import Select from './select';
import FieldDefault from './field-default';

import IconClose from '../icons/icon-close';
import IconArrowDown from '../icons/icon-arrow-down';

const TYPES = [
    {
        id: 'date',
        text: 'date'
    },
    {
        id: 'status',
        text: 'status'
    },
    {
        id: 'completion',
        text: 'completion'
    }
];

const CRON_TYPES = [
    {
        id: 'day',
        text: 'day'
    },
    {
        id: 'week',
        text: 'week'
    },
    {
        id: 'month',
        text: 'month'
    }
];

const CRON_WEEK_DAYS = [
    {
        id: '1',
        text: 'M'
    },
    {
        id: '2',
        text: 'T'
    },
    {
        id: '3',
        text: 'W'
    },
    {
        id: '4',
        text: 'T'
    },
    {
        id: '5',
        text: 'F'
    },
    {
        id: '6',
        text: 'S'
    },
    {
        id: '0',
        text: 'S'
    }
];

const MIDNIGHT_OPTIONS = [
    {
        id: 'am',
        text: 'AM'
    },
    {
        id: 'pm',
        text: 'PM'
    }
];

const CronWeekDayButton = ({ id, text, selected, onClick }) => {
    const handleClick = () => onClick && onClick(id);

    return <button className={'btn btn-input' + (selected ? ' selected' : '')} onClick={handleClick}>{text}</button>;
}

const DatePickerInput = ({ value, placeholder, onClick }) => <button className="btn btn-input wide" onClick={onClick}>{value || placeholder} <IconArrowDown /></button>;

class ResourceRepeatPopout extends PureComponent {
    static defaultProps = {
        trigger: null,
        till: null,
        types: ['date', 'status', 'completion']
    }

    state = {
        type: this.props.type || TYPES[0].id,
        cron: this.props.cron || this.defaultCron,
        ...decodeCronString(this.props.cron),
        trigger: this.props.trigger || (this.props.taskGroups.length ? this.props.taskGroups[0].id : null),
        till: this.props.till ? new Date(this.props.till) : null,
        popoutVisible: false
    }

    handlePopoutOpenClick = () => {
        this.show();
    }

    handlePopoutCloseClick = () => {
        this.popoutCtrl.hide();
    }

    handlePopoutShow = () => {
        this.setState({
            popoutVisible: true
        });
    }

    handlePopoutHide = () => {
        this.setState({
            popoutVisible: false
        });
    }

    handleTypeChange = ({ id }) => {
        this.setState({
            type: id
        });
    }

    handleCronTypeChange = ({ id }) => {
        const cronDayOfWeek = (id === 'week' && this.state.cronDayOfWeek.length > 1) ? [this.state.cronDayOfWeek[0]] : this.state.cronDayOfWeek;

        this.setState({
            cronType: id,
            cronDayOfWeek
        });
    }

    handleCronWeekDayClick = cronWeekDayId => {
        if (this.state.cronType === 'week') {
            this.setState({
                cronDayOfWeek: [cronWeekDayId]
            });
            return;
        }

        this.setState({
            cronDayOfWeek: this.state.cronDayOfWeek.includes(cronWeekDayId) ? this.state.cronDayOfWeek.filter(id => id !== cronWeekDayId) : [...this.state.cronDayOfWeek, cronWeekDayId]
        });
    }

    handleCronMonthDayChange = cronDayOfMonth => {
        this.setState({ cronDayOfMonth });
    }

    handleCronMonthDayBlur = cronDayOfMonth => {
        if (isNaN(cronDayOfMonth)) {
            cronDayOfMonth = 1;
        }

        if (cronDayOfMonth - 0 < 1) {
            cronDayOfMonth = 1;
        }

        if (cronDayOfMonth - 0 > 31) {
            cronDayOfMonth = 31;
        }

        this.setState({ cronDayOfMonth });
    }

    handleMidnightChange = ({ id }) => {
        this.setState({
            cronStartAtMidnight: id
        });
    }

    handleCronStartAtChange = cronStartAt => {
        this.setState({ cronStartAt });
    }

    handleCronStartAtBlur = cronStartAt => {
        if (!cronStartAt.match(/^\d{0,2}:\d{0,2}$/)) {
            cronStartAt = '09:00';
        }

        let cronStartAtMidnight = this.state.cronStartAtMidnight;
        let [hours, minutes] = cronStartAt.split(':');

        if (hours - 0 > 12) {
            cronStartAtMidnight = 'pm';
            hours = Math.min(hours - 12, 12).toString();
        }

        if (minutes - 0 > 60) {
            minutes = '60';
        }

        cronStartAt = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;

        this.setState({
            cronStartAt,
            cronStartAtMidnight
        });
    }

    handleTriggerChange = ({ id }) => {
        this.setState({
            trigger: id
        });
    }

    handleTillChange = till => {
        this.setState({ till });
    }

    handleDisableRepeat = () => {
        this.props.onDisable && this.props.onDisable();

        this.popoutCtrl.hide();
    }

    handleSubmit = () => {
        if (this.state.type === 'date') {
            const cron = encodeCronString(this.state);
            if (cron.error) {
                createAlert('default', cron.message);
                return;
            }

            this.props.onChange({
                type: this.state.type,
                cron,
                till: this.state.till
            });
        } else {
            this.props.onChange({
                type: this.state.type,
                trigger: this.state.type === 'status' ? this.state.trigger : undefined,
                cron: null,
                till: null
            });
        }

        this.popoutCtrl.hide();
    }

    handleCancel = () => {
        this.popoutCtrl.hide();   
    }

    show() {
        this.popoutCtrl.show();
        this.attemptFetch();
    }

    attemptFetch() {
        // this.props.taskGroups.forEach(taskGroup => {
        //     if (!getTasksFetched('taskGroup', taskGroup.id) && !getTasksLoading('taskGroup', taskGroup.id)) {
        //         fetchTasks({
        //             taskGroupId: taskGroup.id
        //         }, true);
        //     }
        // });
    }

    setPopoutCtrl = ctrl => this.popoutCtrl = ctrl;

    get defaultCron() {
        return `0 ${((this.props.currentUser && this.props.currentUser.tz) || 0) / 60 + 9} * * *`;
    }

    get types() {
        return TYPES.filter(type => this.props.types.includes(type.id));
    }

    componentDidUpdate(prevProps) {
        if (prevProps.cron !== this.props.cron) {
            const cron = this.props.cron || this.defaultCron;
            this.setState({
                cron,
                ...decodeCronString(cron)
            });
        }

        if (prevProps.type !== this.props.type) {
            this.setState({
                type: this.props.type || TYPES[0].id
            });
        }

        if (prevProps.till !== this.props.till) {
            this.setState({
                till: this.props.till ? new Date(this.props.till) : null
            });
        }
    }

    render() {
        // TODO: Add content loader
        const children = React.Children.map(this.props.children, child => {
            return React.cloneElement(child, {
                className: child.props.className + (this.state.popoutVisible ? ' selected disable-tooltip' : '')
            });
        });

        return (
            <React.Fragment>
                <div onClick={this.handlePopoutOpenClick}>
                    {children}
                </div>

                <Popout
                    className="no-arrow large"
                    provideController={this.setPopoutCtrl}
                    onShow={this.handlePopoutShow}
                    onHide={this.handlePopoutHide}
                    xAlign={this.props.xAlign}
                    defaultXPosition={this.props.defaultXPosition}
                    closeOnEveryClick={true}
                >
                    <div className="popout-head">
                        {this.props.title}

                        <button className="btn btn-icon btn-small popout-close" onClick={this.handlePopoutCloseClick}>
                            <IconClose />
                        </button>
                    </div>

                    <div className="popout-main">
                        <div className="resource-repeat">
                            {
                                this.props.types.length > 1 ? (
                                    <div className="resource-repeat-row">
                                        <div className="row-label">
                                            Based on
                                        </div>

                                        <div className="row-value">
                                            <div className="row-input">
                                                <Select
                                                    small={true}
                                                    selected={this.state.type}
                                                    options={this.types}
                                                    onChange={this.handleTypeChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : null
                            }

                            {
                                this.state.type === 'date' ? (
                                    <React.Fragment>
                                        <div className="resource-repeat-row">
                                            <div className="row-label">
                                                Every
                                            </div>

                                            <div className="row-value">
                                                <div className="row-input">
                                                    <Select
                                                        small={true}
                                                        selected={this.state.cronType}
                                                        options={CRON_TYPES}
                                                        onChange={this.handleCronTypeChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="resource-repeat-row">
                                            <div className="row-label">
                                                {
                                                    this.state.cronType === 'month' ? 'Date of' : 'On'
                                                }
                                            </div>

                                            <div className="row-value">
                                                {
                                                    this.state.cronType === 'month' ? (
                                                        <React.Fragment>
                                                            <div className="row-input">
                                                                <FieldDefault
                                                                    small={true}
                                                                    value={this.state.cronDayOfMonth}
                                                                    onBlur={this.handleCronMonthDayBlur}
                                                                    onChange={this.handleCronMonthDayChange}
                                                                />
                                                            </div>

                                                            <div className="row-input">
                                                                <small className="input-helper">
                                                                    (For the 3rd, put 3. For the 15th put 15)
                                                                </small>
                                                            </div>
                                                        </React.Fragment>
                                                    ) : (
                                                        <div className="row-input">
                                                            <div className="row-input-button-group">
                                                                {
                                                                    CRON_WEEK_DAYS.map(day => <CronWeekDayButton
                                                                        key={day.id}
                                                                        id={day.id}
                                                                        text={day.text}
                                                                        selected={this.state.cronDayOfWeek.includes(day.id)}
                                                                        onClick={this.handleCronWeekDayClick}
                                                                    />)
                                                                }
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </div>

                                        <div className="resource-repeat-row">
                                            <div className="row-label">
                                                At
                                            </div>

                                            <div className="row-value">
                                                <div className="row-input">
                                                    <FieldDefault
                                                        small={true}
                                                        value={this.state.cronStartAt}
                                                        onBlur={this.handleCronStartAtBlur}
                                                        onChange={this.handleCronStartAtChange}
                                                    />
                                                </div>

                                                <div className="row-input">
                                                    <Select
                                                        small={true}
                                                        selected={this.state.cronStartAtMidnight}
                                                        options={MIDNIGHT_OPTIONS}
                                                        onChange={this.handleMidnightChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="resource-repeat-row">
                                            <div className="row-label">
                                                Ends
                                            </div>

                                            <div className="row-value">
                                                <div className="row-input">
                                                    <DatePicker
                                                        selected={this.state.till}
                                                        showTimeSelect={true}
                                                        minDate={new Date()}
                                                        dateFormat="MMM d, yyyy h:mm a"
                                                        placeholderText="never"
                                                        customInput={<DatePickerInput />}
                                                        onChange={this.handleTillChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                ) : this.state.type === 'completion' ? (
                                    <React.Fragment>
                                        <div className="resource-repeat-row">
                                            When the task is marked completed.
                                        </div>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <div className="resource-repeat-row">
                                            <div className="row-label">
                                                is set to
                                            </div>

                                            <div className="row-value">
                                                <div className="row-input">
                                                    <Select
                                                        small={true}
                                                        selected={this.state.trigger}
                                                        options={this.props.taskGroups.map(taskGroup => ({
                                                            id: taskGroup.id,
                                                            text: taskGroup.name
                                                        }))}
                                                        onChange={this.handleTriggerChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                )
                            }
                        </div>
                    </div>

                    <div className="popout-foot">
                        {
                            this.props.rule ? (
                                <span className="action" onClick={this.handleDisableRepeat}>Disable</span>
                            ) : null
                        }

                        <div className="popout-actions">
                            <button className="btn btn-secondary btn-small" onClick={this.handleCancel}>Cancel</button>

                            <button className="btn btn-success btn-small" onClick={this.handleSubmit}>
                                Set recurring
                            </button>
                        </div>
                    </div>
                </Popout>
            </React.Fragment>
        );
    }
}

const makeMapStateToProps = () => {
    const getCurrentUser = makeGetCurrentUser();
    const getSortedActiveProjectTaskGroups = makeGetSortedActiveProjectTaskGroups();

    return ({ users, taskGroups }, ownProps) => {

        return {
            currentUser: getCurrentUser(users),
            taskGroups: ownProps.projectId ? getSortedActiveProjectTaskGroups(taskGroups.items, ownProps.projectId) : []
        }
    }
}

export default connect(makeMapStateToProps)(ResourceRepeatPopout);
